import { memo } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Award, Briefcase, Lightbulb, ArrowRight, CheckCircle } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import chiSiamoImg from '@/assets/images/sections/ChiSiamo.png'

// Value pillars configuration
const valuePillars = [
  {
    key: 'quality',
    icon: Award,
    color: 'from-amber-500 to-orange-500',
  },
  {
    key: 'professionalism',
    icon: Briefcase,
    color: 'from-blue-500 to-indigo-500',
  },
  {
    key: 'innovation',
    icon: Lightbulb,
    color: 'from-emerald-500 to-teal-500',
  },
]

// Value Pillar Card
const ValuePillar = memo(function ValuePillar({
  pillar,
  index,
  t,
}: {
  pillar: (typeof valuePillars)[0]
  index: number
  t: (key: string) => string
}) {
  const Icon = pillar.icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="flex items-center gap-4 p-4 rounded-xl bg-white/80 backdrop-blur-sm border border-white/50 shadow-sm hover:shadow-md transition-shadow"
    >
      <div
        className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center shadow-md`}
      >
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <h4 className="font-bold text-gray-900">{t(`about.values.${pillar.key}.title`)}</h4>
        <p className="text-sm text-gray-600">{t(`about.values.${pillar.key}.description`)}</p>
      </div>
    </motion.div>
  )
})

export function AboutSection() {
  const { t } = useTranslation('home')

  return (
    <section className="section-padding bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 opacity-60 blur-3xl -translate-x-1/2 -translate-y-1/2" />

      <div className="container-custom relative">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-gray-100 text-gray-700 mb-4 sm:mb-6">
              <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-medium">{t('about.label')}</span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              {t('about.title')}
            </h2>

            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3 sm:mb-4">
              {t('about.description')}
            </p>

            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
              {t('about.text')}
            </p>

            {/* Highlights */}
            <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
              {(t('about.highlights', { returnObjects: true }) as string[]).map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-2 sm:gap-3"
                >
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">{item}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <LocalizedLink
              to="about"
              className="inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white text-sm sm:text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              <span>{t('about.learnMore')}</span>
              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-1" />
            </LocalizedLink>
          </motion.div>

          {/* Image + Values */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Main Image */}
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl mb-4 sm:mb-6">
              <img
                src={chiSiamoImg}
                alt="BF Wellness Team"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent" />

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 bg-white/90 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-lg"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                    <Award className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-xs sm:text-sm">{t('about.badge.year')}</div>
                    <div className="text-[10px] sm:text-xs text-gray-600">{t('about.badge.title')}</div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 opacity-20 blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 opacity-10 blur-2xl" />

            {/* Value Pills - Hidden on mobile for cleaner look */}
            <div className="hidden lg:block space-y-3">
              {valuePillars.map((pillar, index) => (
                <ValuePillar
                  key={pillar.key}
                  pillar={pillar}
                  index={index}
                  t={t as (key: string) => string}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
